import {
	Component, Input, Output, EventEmitter, SimpleChanges, OnDestroy,
	OnChanges
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { ProtocolSettings, ProtocolSettingsTypes, PrtgSettings } from 'DiscoveryEngine.TS/Protocols';
import { Guid, StringUtils } from "DiscoveryEngine.TS/Utils";
import { ComponentBase, Changeable } from "common";
import { NavigationService } from 'netview';
import { DiscResultsService } from 'discovery';
import { NetViewDto } from 'UvExplorerServer.Dtos.TS/NetViews';
import { IdentityService, LicensingService } from 'identity';
import { MessageService } from 'base';
import { ToolsService } from 'tools';
import { AssetPandaExport } from 'DiscoveryEngine.TS/Export';
import { ConfigurationService } from '../../../configuration';
import { AgentDto } from '../../../UvExplorerServer.Dtos.TS/Agents';
import { ScheduledDiscovery } from '../../../DiscoveryEngine.TS/Discovery';


@Component({
	selector: 'assetpanda-export-config',
	templateUrl: '../assetpanda-export-config/assetpanda-export-config.component.html',
	styleUrls: ['../assetpanda-export-config/assetpanda-export-config.component.scss']
})
export class AssetPandaExportConfigComponent extends ComponentBase implements OnDestroy, OnChanges, Changeable {

	@Input()
	public Network: NetViewDto;

	@Input()
	public AssetPandaExport: AssetPandaExport;

	@Input()
	public Agent: AgentDto;

	@Input()
	public ScheduledDiscovery: ScheduledDiscovery;

	@Input()
	public EnableExportOrigin: boolean;

	public SettingsForm: UntypedFormGroup;

	public EnableExport: UntypedFormControl;

	public TargetServerApiURL: UntypedFormControl;
	public TargetServerUsername: UntypedFormControl;
	public TargetServerPassword: UntypedFormControl;
	public BuildingName: UntypedFormControl;
	public RoomName: UntypedFormControl;

	public ExportOrigin: UntypedFormControl;

	public ExportDevices: UntypedFormControl;
	public ExportConfigs: UntypedFormControl;
	public ExportPasswords: UntypedFormControl;

	private passwordChanged: boolean;
	private passwordChangedSub: Subscription;

	constructor(private fb: UntypedFormBuilder,
		private navService: NavigationService,
		private discResultsService: DiscResultsService,
		private identityService: IdentityService,
		private messageService: MessageService,
		private configService: ConfigurationService,
		private toolsService: ToolsService) {

		super();

		this.Network = null;
		this.AssetPandaExport = null;
		this.EnableExportOrigin = false;
	}

	public ngOnDestroy(): void {
		if (this.passwordChangedSub) {
			this.passwordChangedSub.unsubscribe();
		}
	}

	public get isLicenseValid(): boolean {
		//return this.licensingService.isLicenseValid;
		return true;
	}

	protected BuildForm(): void {

		let enableExport = false;

		// AssetPanda settings
		let serverApiURL = "";
		let serverUsername = "";
		let serverPassword = "";
		let buildingName = "";
		let roomName = "";

		let exportOrigin = "";

		let exportDevices = false;
		let exportConfigs = false;

		if (this.AssetPandaExport) {
			let value = this.AssetPandaExport;

			enableExport = value.EnableExport;

			if (value.HasTargetServerApiURL)
				serverApiURL = value.TargetServerApiURL;
			if (value.HasTargetServerUsername)
				serverUsername = value.TargetServerUsername;
			if (value.HasTargetServerPassword)
				serverPassword = this.hidePassword(value.TargetServerPassword);
			if (value.HasBuildingName)
				buildingName = value.BuildingName;
			if (value.HasRoomName)
				roomName = value.RoomName;

			if (value.HasExportOrigin)
				exportOrigin = value.ExportOrigin;

			exportDevices = value.ExportDevices;
			exportConfigs = value.ExportConfigs;
		}

		this.EnableExport = new UntypedFormControl(enableExport);

		this.TargetServerApiURL = new UntypedFormControl(serverApiURL);
		this.TargetServerUsername = new UntypedFormControl(serverUsername);
		this.TargetServerPassword = new UntypedFormControl(serverPassword);
		this.BuildingName = new UntypedFormControl(buildingName);
		this.RoomName = new UntypedFormControl(roomName);

		this.ExportOrigin = new UntypedFormControl(exportOrigin);

		this.ExportDevices = new UntypedFormControl(exportDevices);
		this.ExportConfigs = new UntypedFormControl(exportConfigs);

		this.passwordChanged = false;
		this.passwordChangedSub = this.TargetServerPassword.valueChanges.subscribe(() => {
			this.passwordChanged = true;
		});

		this.SettingsForm = this.fb.group({
			EnableExport: this.EnableExport,
			TargetServerApiURL: this.TargetServerApiURL,
			TargetServerUsername: this.TargetServerUsername,
			TargetServerPassword: this.TargetServerPassword,
			BuildingName: this.BuildingName,
			RoomName: this.RoomName,
			ExportOrigin: this.ExportOrigin,
			ExportDevices: this.ExportDevices,
			ExportConfigs: this.ExportConfigs,
		},
		{
			validator: Validators.compose([
				this.validAssetPandaSettings(this.EnableExport, this.TargetServerApiURL, this.TargetServerUsername, this.TargetServerPassword)
			])
		});
	}

	public get ExportEnabled(): boolean {
		if (this.EnableExport) {
			return this.EnableExport.value;
		}
		else {
			return false;
		}
	}

	public ngOnInit(): void {
		this.BuildForm();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.BuildForm();
	}

	public get tempChanges(): boolean {
		return this.SettingsForm.dirty;
	}

	public get tempChangesValid(): boolean {
		return this.SettingsForm.valid;
	}

	public discardTempChanges(): void {
		this.BuildForm();
	}

	public applyTempChanges(): void {
		if (this.AssetPandaExport) {
			let value = this.AssetPandaExport;

			value.EnableExport = this.EnableExport.value;

			value.TargetServerApiURL = this.TargetServerApiURL.value;
			value.TargetServerUsername = this.TargetServerUsername.value;
			if (this.passwordChanged) {
				value.TargetServerPassword = StringUtils.ObfuscateString2(this.TargetServerPassword.value);
			}
			value.BuildingName = this.BuildingName.value;
			value.RoomName = this.RoomName.value;

			value.ExportOrigin = this.ExportOrigin.value;

			value.ExportDevices = this.ExportDevices.value;
			value.ExportConfigs = this.ExportConfigs.value;
			
			this.BuildForm();
		}
	}

	public onPasswordKeyDownHandler(event: any): void {
		if (!this.passwordChanged) {
			this.passwordChanged = true;

			this.TargetServerPassword.setValue('');
			this.TargetServerPassword.markAsDirty();
		}
	}

	public get invalidTempChanges(): string[] {
		return ["AssetPanda Export"];
	}

	private validAssetPandaSettings(enableExport: UntypedFormControl, serverApiURL: UntypedFormControl, serverUsername: UntypedFormControl, serverPassword: UntypedFormControl): ValidatorFn {
		return () => {
			if (enableExport.value) {
				if (!serverApiURL.value) {
					return {
						validAssetPandaSettings: { valid: false }
					};
				}
				else if (!serverUsername.value) {
					return {
						validAssetPandaSettings: { valid: false }
					};
				}
				else if (!serverPassword.value) {
					return {
						validAssetPandaSettings: { valid: false }
					};
				}
				else {
					return null;
				}
			}
		}
	}

	public ExportNow(): void {
		if (this.tempChanges) {
			this.messageService.displayInfoMessage(
				"You have unsaved changes to your AssetPanda export settings.\nPlease save before exporting.", 10000);
		}
		else {
			if (!!this.Network && !!this.Network.Guid) {
				let sessionGuid = this.identityService.sessionGuid;
				let netViewGuid = this.Network.Guid;
				let agentGuid = this.navService.location.agentGuid;
				let schedDiscGuid = this.navService.location.schedDiscGuid;
				this.discResultsService.runAssetPandaExport(sessionGuid, netViewGuid, agentGuid, schedDiscGuid).subscribe(
					(result: boolean) => {
						this.messageService.displayInfoMessage("AssetPanda Export has been initiated");
					},
					(error) => {
						this.messageService.displayErrorMessage("AssetPanda Export failed");
					});
			}
		}
	}

	public get enableTestAssetPandaButton(): boolean {
		return (!!this.TargetServerApiURL && !!this.TargetServerApiURL.value
			&& !!this.TargetServerUsername && !!this.TargetServerUsername.value
			&& !!this.TargetServerPassword && !!this.TargetServerPassword.value);
	}

	public testAssetPandaConnect(): void {
		let password = "";
		if (this.passwordChanged) {
			password = StringUtils.ObfuscateString2(this.TargetServerPassword.value);
		}
		else {
			password = this.AssetPandaExport.TargetServerPassword;
		}
		this.configService.testAssetPandaConnection(this.TargetServerApiURL.value, this.TargetServerUsername.value, password).subscribe(
			() => {
				this.messageService.displayInfoMessage("Successfully connected to Asset Panda");
			},
			(error) => {
				this.messageService.displayErrorMessage("Failed to connect to Asset Panda");
			});
	}
}
